/**
 * Browser auto-translation (Chrome/Google Translate) and some extensions wrap
 * text nodes in <font> elements behind React's back. When React later updates
 * or removes those nodes it throws "Failed to execute 'removeChild' on 'Node'"
 * and the whole page falls into the error screen.
 *
 * This guard makes removeChild/insertBefore tolerant of nodes that have been
 * moved by a third party, so the page keeps working instead of crashing.
 * See https://github.com/facebook/react/issues/11538
 */
export function installDomTranslationGuard() {
  if (typeof Node !== "function" || !Node.prototype) return;
  const proto = Node.prototype as Node & { __lcsGuarded?: boolean };
  if (proto.__lcsGuarded) return;
  proto.__lcsGuarded = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      console.warn("[dom-guard] removeChild skipped: node moved by a third party (e.g. browser translation)");
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      console.warn("[dom-guard] insertBefore fallback: reference node moved by a third party");
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
